// EditPortfolioModal
import Button from 'components/common/Button';
import Icon from 'components/common/Icon';
import Modal from 'components/common/Modal';
import UppyDragDrop from 'components/common/UppyDragDrop';
import React, { useEffect, useState } from 'react';
import { ContentUploadPreview, MediaItem } from 'types/content';
import styles from './EditPortfolioModal.module.sass';
import { getKeysWithUpdatedValues } from 'utils/object';
import Loader from 'components/common/Loader';
import cn from 'classnames';
import ImageKitImage from 'components/common/ImageKitImage';

type NewContentModal = {
  initialMedia?: undefined;
  type: 'new-content';
  handleSave: (updatedItem: MediaItem) => void;
  handleUpdate?: undefined;
};

type UpdateContentModal = {
  initialMedia: MediaItem;
  type: 'update-content';
  handleUpdate: (updatedItem: MediaItem) => void;
  handleSave?: undefined;
};

export type EditPortfolioModalProps = {
  open: boolean;
  onClose: () => void;
} & (NewContentModal | UpdateContentModal);

const EditPortfolioModal: React.FC<EditPortfolioModalProps> = ({
  open,
  onClose,
  handleSave,
  handleUpdate,
  initialMedia,
  type,
}) => {
  const [media, setMedia] = useState<MediaItem | undefined>(initialMedia);
  const [newContent, setNewContent] = useState<ContentUploadPreview>();
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    setIsUploading(false);
    setMedia(initialMedia);
  }, [open, initialMedia]);

  const handleUploadSuccess = (content: ContentUploadPreview) => {
    setNewContent(content);
  };

  const handleRemoveNewContent = () => {
    setNewContent(undefined);
  };

  const handleClose = () => {
    const updatedValues = getKeysWithUpdatedValues(
      media || {},
      initialMedia || {}
    );
    if (updatedValues.length > 0) {
      const close = window.confirm(
        'You are about to leave without saving. Do you want to continue?'
      );
      if (!close) {
        return;
      }
    }
    setIsUploading(false);
    handleRemoveNewContent();
    onClose();
  };

  const savePortfolioItem = () => {
    if (
      type === 'new-content' &&
      typeof newContent !== 'undefined' &&
      typeof handleSave !== 'undefined'
    ) {
      const { url, type } = newContent;
      handleSave({ url, type });
    } else if (
      type === 'update-content' &&
      typeof media !== 'undefined' &&
      typeof handleUpdate !== 'undefined'
    ) {
      handleUpdate(media);
    }
  };

  let saveDisabled = true;
  if (type === 'new-content') {
    saveDisabled = isUploading || typeof newContent === 'undefined';
  } else if (type === 'update-content') {
    const updatedValues = getKeysWithUpdatedValues(
      media || {},
      initialMedia || {}
    );
    saveDisabled = updatedValues.length > 0;
  }

  return (
    <Modal
      open={open}
      onBackdropClick={handleClose}
      className={styles.editPortfolioModal}
    >
      <div className={styles.editPortfolioCard}>
        <div className={styles.headerRow}>
          <h5>{type === 'new-content' ? 'Portfolio Item' : 'Update Title'}</h5>
          <span onClick={handleClose}>
            <Icon name="close" size={20} />
          </span>
        </div>
        {type === 'new-content' && (
          <>
            <UppyDragDrop
              uppyProps={{
                uppyId: 'creator-profile-portfolio-drag-drop',
                uploadType: 'portfolio',
                onUploadSuccess: handleUploadSuccess,
                onRemove: handleRemoveNewContent,
                setIsUploading: setIsUploading,
                restrictions: {
                  maxNumberOfFiles: 1,
                },
              }}
              containerClassName={cn(styles.uploadContentContainer, {
                [styles.hide]: typeof newContent !== 'undefined' || isUploading,
              })}
              copy={{
                dropHereOr: '',
                browse: '',
              }}
            >
              <Button
                className={styles.uploadButton}
                type="light"
                icon="upload"
              >
                Click or drop item (JPEGs, PNGs, GIFs and MP4s)
              </Button>
            </UppyDragDrop>
            {isUploading && (
              <div className={styles.loader}>
                <Loader />
              </div>
            )}
          </>
        )}
        {(typeof newContent !== 'undefined' ||
          typeof media !== 'undefined') && (
          <div className={styles.contentContainer}>
            {typeof newContent !== 'undefined' && (
              <div
                className={styles.removeItem}
                onClick={newContent.removeItem}
              >
                <Icon name="trash" size={12} />
              </div>
            )}
            <ImageKitImage
              path={(media || newContent)!.url}
              alt={(media || newContent)!.url}
              videoPreview={(media || newContent)!.type === 'video'}
            />
          </div>
        )}
        <hr />
        <Button
          onClick={savePortfolioItem}
          fullWidth={false}
          className={styles.saveButton}
          type="primary"
          disabled={saveDisabled}
        >
          Save
          <Icon name="arrow-forward" />
        </Button>
      </div>
    </Modal>
  );
};

export default EditPortfolioModal;
